import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';

import { type Repub } from '@cx-modules/constants';

import { type BettingInsightsUiSearchAttribute, type EsIndex } from '~/configuration/types';
import { useSearchWithAutocomplete } from '~/hooks/useSearchWithAutocomplete.ts';

import styles from './Autocomplete.module.scss';
import { AutocompleteDropdown } from './AutocompleteDropdown';

export function Autocomplete<TIndex extends EsIndex>({
  disabled = false,
  indexName,
  placeholder,
  repub,
  searchAttributes,
  searchOperator = 'AND',
}: {
  disabled?: boolean;
  indexName: TIndex;
  placeholder?: string;
  repub: Repub;
  searchAttributes: BettingInsightsUiSearchAttribute<TIndex>[];
  searchOperator?: 'AND' | 'OR';
}) {
  const [inputValue, setInputValue] = useState('');
  const [_, setInputIsFocused] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [selectedTagIndex, setSelectedTagIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const autoCompletePlaceholder = placeholder ?? t('CX_PLUS_SEARCH_PLACEHOLDER');

  const {
    activeIndex,
    getInputProps,
    handleClearSearchResults,
    handleKeyDown,
    searchResults,
    selectedTags,
    setActiveIndex,
    setSelectedTags,
  } = useSearchWithAutocomplete({
    inputRef,
    indexName,
    repub,
    searchAttributes,
    searchOperator,
    setInputValue,
    setIsDropdownVisible,
  });

  const handleBlur = useCallback(() => {
    setInputIsFocused(false);
  }, []);

  const handleFocus = useCallback(() => {
    setInputIsFocused(true);
    setInputValue(value => value);
  }, []);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownVisible(false);
      }
    },
    [setIsDropdownVisible],
  );

  const handleCloseDropdown = useCallback(() => {
    setIsDropdownVisible(false);
  }, [setIsDropdownVisible]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleTagSelection = useCallback(
    (tag: string) => {
      if (!selectedTags.includes(tag)) {
        setSelectedTags(tags => [...tags, tag]);
        setIsDropdownVisible(false);
        setActiveIndex(-1);
        setInputValue('');
        setSelectedTagIndex(-1);
        inputRef.current?.focus();
      }
      handleClearSearchResults();
    },
    [handleClearSearchResults, selectedTags, setActiveIndex, setSelectedTags],
  );

  const handleTagRemoval = useCallback(
    (tagToRemove: string, index?: number) => {
      setSelectedTags(selectedTags.filter(tag => tag !== tagToRemove));
      if (typeof index === 'number') {
        setSelectedTagIndex(Math.max(-1, index - 1));
      }
    },
    [setSelectedTags, selectedTags],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowLeft') {
        if (selectedTagIndex >= 0) {
          e.preventDefault();
          setSelectedTagIndex(index => Math.max(0, index - 1));
        } else if (inputRef.current?.selectionStart === 0) {
          e.preventDefault();
          setSelectedTagIndex(selectedTags.length - 1);
        }
        return;
      }

      if (e.key === 'ArrowRight') {
        if (selectedTagIndex >= 0) {
          e.preventDefault();
          setSelectedTagIndex(index => (index < selectedTags.length - 1 ? index + 1 : -1));
        }
        return;
      }

      if (e.key === 'Backspace') {
        if (selectedTagIndex >= 0) {
          e.preventDefault();
          handleTagRemoval(selectedTags[selectedTagIndex], selectedTagIndex);
        } else if (inputValue.length === 0 && selectedTags.length > 0) {
          const lastTag = selectedTags[selectedTags.length - 1];
          handleTagRemoval(lastTag);
        }
        return;
      }

      handleKeyDown(e, () => {
        if (activeIndex !== -1 && searchResults[activeIndex]) {
          handleTagSelection(searchResults[activeIndex]);
        } else if (searchResults.length === 1) {
          handleTagSelection(searchResults[0]);
        }
      });
    },
    [
      activeIndex,
      handleKeyDown,
      handleTagRemoval,
      handleTagSelection,
      inputValue.length,
      searchResults,
      selectedTags,
      selectedTagIndex,
    ],
  );

  const handleClearSearch = useCallback(() => {
    setInputValue('');
    setSelectedTags([]);
    setSelectedTagIndex(-1);
    handleClearSearchResults();
  }, [handleClearSearchResults, setInputValue, setSelectedTags]);

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.searchWrapper}>
        <div className={styles.iconContainer}>
          <Search className={disabled ? styles.iconDisabled : styles.icon} size={20} />
        </div>
        <div className={styles.tagsContainer}>
          {selectedTags.map((tag, index) => (
            <span className={`${styles.tag} ${selectedTagIndex === index ? styles.tagSelected : ''}`} key={tag}>
              {tag}
              <button className={styles.tagButton} onClick={() => handleTagRemoval(tag)}>
                <X size={14} />
              </button>
            </span>
          ))}
          <input
            {...getInputProps()}
            className={`${styles.input} ${selectedTagIndex >= 0 ? styles.inputCaretHidden : ''}`}
            data-testid="search-input"
            disabled={disabled}
            onBlur={handleBlur}
            onFocus={() => {
              handleFocus();
              setSelectedTagIndex(-1);
            }}
            onKeyDown={handleInputKeyDown}
            placeholder={selectedTags.length === 0 ? autoCompletePlaceholder : ''}
            ref={inputRef}
            type="text"
            value={inputValue}
          />
        </div>
        {selectedTags.length > 0 || inputValue.length > 0 ? (
          <div className={styles.clearContainer}>
            <button className={styles.clearButton} onClick={handleClearSearch}>
              <X className={styles.clearIcon} size={20} />
            </button>
          </div>
        ) : null}
      </div>
      {isDropdownVisible && searchResults.length > 0 && (
        <AutocompleteDropdown
          activeIndex={activeIndex}
          handleCloseDropdown={handleCloseDropdown}
          onSelect={handleTagSelection}
          results={searchResults}
        />
      )}
    </div>
  );
}
